import React, { useEffect, useState } from "react";
import "./RSVPPage.css";
import { db } from "./firebase";
import { collection, doc, writeBatch, serverTimestamp } from "firebase/firestore";

const EVENT_DATE = new Date("2026-10-19T08:30:00+05:30");
const COLLECTION_NAME = "event-registrations";
const MAX_PARTICIPANTS = 20;

const emptyParticipant = () => ({ name: "", grade: "", email: "", phone: "" });

const sanitizeText = (str, maxLen = 120) =>
  (str || "")
    .replace(/[<>]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, maxLen);

const sanitizeDigits = (str, maxLen = 10) =>
  (str || "").replace(/\D/g, "").slice(0, maxLen);

function getTimeLeft() {
  const diff = Math.max(0, EVENT_DATE.getTime() - Date.now());
  return {
    days: Math.floor(diff / 86400000),
    hrs: Math.floor((diff / 3600000) % 24),
    min: Math.floor((diff / 60000) % 60),
    sec: Math.floor((diff / 1000) % 60),
  };
}

const pad = (n) => String(n).padStart(2, "0");

export default function RSVPPage() {
  const [timeLeft, setTimeLeft] = useState(getTimeLeft());
  const [school, setSchool] = useState("");
  const [parentsAttending, setParentsAttending] = useState("no");
  const [parentCount, setParentCount] = useState("");
  const [participants, setParticipants] = useState([emptyParticipant()]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    const t = setInterval(() => setTimeLeft(getTimeLeft()), 1000);
    return () => clearInterval(t);
  }, []);

  const updateParticipant = (idx, field, value) => {
    setParticipants((prev) =>
      prev.map((p, i) => (i === idx ? { ...p, [field]: value } : p))
    );
    if (errorMessage) setErrorMessage("");
  };

  const addParticipant = () => {
    if (participants.length >= MAX_PARTICIPANTS) return;
    setParticipants((prev) => [...prev, emptyParticipant()]);
  };

  const removeParticipant = (idx) => {
    setParticipants((prev) => prev.filter((_, i) => i !== idx));
  };

  const validate = () => {
    const cleanSchool = sanitizeText(school, 120);
    if (!cleanSchool) return "Please enter your school / institution.";

    if (parentsAttending === "yes") {
      const count = parseInt(parentCount, 10);
      if (!parentCount || isNaN(count) || count < 1 || count > 20) {
        return "Please specify a valid number of parents (between 1 and 20).";
      }
    }

    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRe = /^\d{10}$/;

    for (let i = 0; i < participants.length; i++) {
      const p = participants[i];
      const label = `Attendee ${pad(i + 1)}`;
      const cleanName = sanitizeText(p.name, 80);
      const cleanGrade = sanitizeText(p.grade, 15);
      const cleanEmail = (p.email || "").trim().toLowerCase();
      const cleanPhone = (p.phone || "").replace(/\D/g, "");

      if (!cleanName) return `${label}: full name is required.`;
      if (!cleanGrade) return `${label}: grade is required.`;
      if (!emailRe.test(cleanEmail)) return `${label}: enter a valid email address.`;
      if (!phoneRe.test(cleanPhone)) {
        return `${label}: contact number must be exactly 10 digits (e.g. 0771234567).`;
      }
    }
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      setErrorMessage(err);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");
    try {
      const batch = writeBatch(db);
      const colRef = collection(db, COLLECTION_NAME);
      const groupId = doc(colRef).id; // shared ID linking people submitted together
      const trimmedSchool = sanitizeText(school, 120);
      const parentsComing = parentsAttending === "yes";
      const countNum = parentsComing ? Math.min(20, Math.max(1, parseInt(parentCount, 10) || 1)) : 0;

      participants.forEach((p, i) => {
        batch.set(doc(colRef), {
          school: trimmedSchool,
          name: sanitizeText(p.name, 80),
          grade: sanitizeText(p.grade, 15),
          email: (p.email || "").trim().toLowerCase().slice(0, 100),
          phone: (p.phone || "").replace(/\D/g, "").slice(0, 10),
          parentsAttending: parentsComing,
          numberOfParents: countNum,
          parentCount: countNum,
          groupId,
          groupSize: participants.length,
          positionInGroup: i + 1,
          createdAt: serverTimestamp(),
        });
      });

      await batch.commit();
      setSubmitted({ count: participants.length, school: trimmedSchool, groupId, totalParents: countNum });
    } catch (error) {
      console.error("RSVP submission failed:", error);
      setErrorMessage("Transmission failed. Please check your connection and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setSchool("");
    setParentsAttending("no");
    setParentCount("");
    setParticipants([emptyParticipant()]);
    setSubmitted(null);
  };

  return (
    <div className="rsvp-container">
      <div className="rsvp-container-outer">
        <div className="rsvp-container-inner">
          <div className="rsvp-header-label">BTUI'26 / RSVP</div>

          <h1 className="rsvp-hero-title">
            CONFIRM YOUR <span className="rsvp-accent">ATTENDANCE</span>
          </h1>

          <p className="rsvp-hero-desc">
            Join us for <strong>Beyond the User Interface 2026</strong>, the prestigious Annual ICT
            Day organized by the Royal College Computer Society. Taking place on{" "}
            <strong>19th October 2026</strong> at Royal College, featuring futuristic exhibitions,
            keynotes from industry pioneers, and nationwide competition award ceremonies.
          </p>

          {/* Event banner */}
          <div className="rsvp-event-card">
            <div className="rsvp-event-info">
              <span className="rsvp-tag">
                <span className="rsvp-tag-dot" /> ANNUAL ICT DAY // OCTOBER 19
              </span>
              <h2 className="rsvp-event-date">MONDAY, 19TH OCTOBER 2026</h2>
              <div className="rsvp-event-meta">
                <span>◷ 08:30 AM ONWARDS</span>
                <span className="rsvp-meta-sep">•</span>
                <span>⌖ ROYAL COLLEGE, COLOMBO</span>
              </div>
            </div>
            <div className="rsvp-countdown">
              <div className="rsvp-countdown-label">T-MINUS EVENT LAUNCH</div>
              <div className="rsvp-countdown-boxes">
                {[
                  ["days", "DAYS"],
                  ["hrs", "HRS"],
                  ["min", "MIN"],
                  ["sec", "SEC"],
                ].map(([key, label], i) => (
                  <React.Fragment key={key}>
                    {i > 0 && <span className="rsvp-countdown-colon">:</span>}
                    <div className="rsvp-countdown-box">
                      <span className="rsvp-countdown-value">{pad(timeLeft[key])}</span>
                      <span className="rsvp-countdown-unit">{label}</span>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>

          {/* Form / success panel */}
          <div className="rsvp-form-panel">
            {submitted ? (
              <div className="rsvp-success">
                <div className="rsvp-panel-header">
                  <span className="rsvp-panel-title">02 // REGISTRATION CONFIRMED</span>
                  <span className="rsvp-status-badge">TRANSMISSION OK</span>
                </div>
                <h3 className="rsvp-success-title">SEE YOU THERE.</h3>
                <p className="rsvp-success-text">
                  {submitted.count} attendee{submitted.count > 1 ? "s" : ""} from{" "}
                  <strong>{submitted.school}</strong> registered for BTUI'26.
                  {submitted.totalParents > 0 && (
                    <> (accompanying parents: {submitted.totalParents})</>
                  )}
                </p>
                <p className="rsvp-success-ref">REF: {submitted.groupId.toUpperCase()}</p>
                <button type="button" className="rsvp-btn rsvp-btn-secondary" onClick={resetForm}>
                  REGISTER ANOTHER GROUP
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                <div className="rsvp-panel-header">
                  <span className="rsvp-panel-title">01 // ATTENDEE REGISTRATION</span>
                  <span className="rsvp-status-badge">REGISTRATIONS OPEN</span>
                </div>

                <div className="rsvp-field rsvp-field-full">
                  <label htmlFor="rsvp-school">
                    SCHOOL / INSTITUTION <span className="rsvp-req">*</span>
                  </label>
                  <input
                    id="rsvp-school"
                    type="text"
                    maxLength={120}
                    placeholder="e.g. Royal College, Colombo"
                    value={school}
                    onChange={(e) => {
                      setSchool(e.target.value.replace(/[<>]/g, "").slice(0, 120));
                      if (errorMessage) setErrorMessage("");
                    }}
                    autoComplete="organization"
                  />
                  <small>Your school, university, or affiliated organization</small>
                </div>

                <div className="rsvp-grid rsvp-parents-row">
                  <div className="rsvp-field">
                    <label htmlFor="rsvp-parents">
                      ARE PARENTS COMING? <span className="rsvp-req">*</span>
                    </label>
                    <select
                      id="rsvp-parents"
                      value={parentsAttending}
                      onChange={(e) => {
                        const val = e.target.value === "yes" ? "yes" : "no";
                        setParentsAttending(val);
                        if (val === "yes" && !parentCount) {
                          setParentCount("1");
                        }
                        if (errorMessage) setErrorMessage("");
                      }}
                    >
                      <option value="no">NO</option>
                      <option value="yes">YES</option>
                    </select>
                    <small>Will parents or guardians accompany your group?</small>
                  </div>

                  {parentsAttending === "yes" && (
                    <div className="rsvp-field">
                      <label htmlFor="rsvp-parent-count">
                        NUMBER OF PARENTS <span className="rsvp-req">*</span>
                      </label>
                      <input
                        id="rsvp-parent-count"
                        type="number"
                        min="1"
                        max="20"
                        maxLength={2}
                        inputMode="numeric"
                        placeholder="e.g. 1 or 2"
                        value={parentCount}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, "").slice(0, 2);
                          setParentCount(val);
                          if (errorMessage) setErrorMessage("");
                        }}
                      />
                      <small>Total number of parents / guardians attending (1–20)</small>
                    </div>
                  )}
                </div>

                {participants.map((p, idx) => (
                  <div className="rsvp-participant" key={idx}>
                    <div className="rsvp-participant-header">
                      <span>ATTENDEE {pad(idx + 1)}</span>
                      {participants.length > 1 && (
                        <button
                          type="button"
                          className="rsvp-remove-btn"
                          onClick={() => removeParticipant(idx)}
                          aria-label={`Remove attendee ${idx + 1}`}
                        >
                          ✕ REMOVE
                        </button>
                      )}
                    </div>
                    <div className="rsvp-grid">
                      <div className="rsvp-field">
                        <label htmlFor={`rsvp-name-${idx}`}>
                          FULL NAME <span className="rsvp-req">*</span>
                        </label>
                        <input
                          id={`rsvp-name-${idx}`}
                          type="text"
                          maxLength={80}
                          placeholder="e.g. Peter Parker"
                          value={p.name}
                          onChange={(e) =>
                            updateParticipant(
                              idx,
                              "name",
                              e.target.value.replace(/[<>]/g, "").slice(0, 80)
                            )
                          }
                          autoComplete="name"
                        />
                        <small>Enter your full official name</small>
                      </div>
                      <div className="rsvp-field">
                        <label htmlFor={`rsvp-grade-${idx}`}>
                          GRADE <span className="rsvp-req">*</span>
                        </label>
                        <input
                          id={`rsvp-grade-${idx}`}
                          type="text"
                          maxLength={15}
                          placeholder="e.g. 11"
                          value={p.grade}
                          onChange={(e) =>
                            updateParticipant(
                              idx,
                              "grade",
                              e.target.value.replace(/[<>]/g, "").slice(0, 15)
                            )
                          }
                        />
                        <small>Current grade or year of study</small>
                      </div>
                      <div className="rsvp-field">
                        <label htmlFor={`rsvp-email-${idx}`}>
                          EMAIL ADDRESS <span className="rsvp-req">*</span>
                        </label>
                        <input
                          id={`rsvp-email-${idx}`}
                          type="email"
                          maxLength={100}
                          placeholder="e.g. peter.parker@example.com"
                          value={p.email}
                          onChange={(e) =>
                            updateParticipant(idx, "email", e.target.value.trim().slice(0, 100))
                          }
                          autoComplete="email"
                          autoCapitalize="none"
                          autoCorrect="off"
                        />
                        <small>Event updates will be sent here</small>
                      </div>
                      <div className="rsvp-field">
                        <label htmlFor={`rsvp-phone-${idx}`}>
                          CONTACT NUMBER <span className="rsvp-req">*</span>
                        </label>
                        <input
                          id={`rsvp-phone-${idx}`}
                          type="tel"
                          inputMode="numeric"
                          maxLength={10}
                          placeholder="e.g. 0771234567"
                          value={p.phone}
                          onChange={(e) =>
                            updateParticipant(idx, "phone", sanitizeDigits(e.target.value, 10))
                          }
                          autoComplete="tel"
                        />
                        <small>10-digit contact number (e.g. 0771234567)</small>
                      </div>
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  className="rsvp-add-btn"
                  onClick={addParticipant}
                  disabled={participants.length >= MAX_PARTICIPANTS}
                >
                  <span className="rsvp-add-icon">+</span> ADD ANOTHER ATTENDEE
                </button>

                {errorMessage && <div className="rsvp-error">⚠ {errorMessage}</div>}

                <div className="rsvp-actions">
                  <span className="rsvp-count">
                    {pad(participants.length)} ATTENDEE{participants.length > 1 ? "S" : ""} QUEUED
                  </span>
                  <button type="submit" className="rsvp-btn" disabled={isSubmitting}>
                    {isSubmitting ? "TRANSMITTING..." : "CONFIRM ATTENDANCE →"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

